import React, {
    useState,
    useEffect,
    useRef,
    forwardRef,
    useImperativeHandle,
} from 'react';
import './SellerLegalDocsInfoInputForm.scss';
import { Form, Input } from '@availity/form';
import { Col, FormGroup, Row } from 'reactstrap';
import { useDispatch } from 'react-redux';
import { useStateSelector } from '../../../store/selectors';
import {
    getAllStatesIndexed,
    LocationStateItem,
} from '../../../utils/locationsHelper';
import { FormikValues } from 'formik';
import { MarketplaceSellerProfile } from '../../../slices/profiles-data-slice/models/marketplace-seller-profile';
import {
    createEmptySellerDocsInfo,
    SellerDocsInfo,
} from '../../../slices/profiles-data-slice/models/seller-docs-info';
import { getSellerDocsInfoValidationSchema } from '../MarketplaceSellerProfile.validation';
import { UpdateProfile } from '../../../slices/profiles-data-slice/thunks/update-profile.thunk';
import { LegalEntityTypeList } from '../../../slices/marketplace/timeline/models/enums/legal-enity-type';
import SellerDocsInfoAddressInputFields from '../SellerDocsInfoAddressInputFields/SellerDocsInfoAddressInputFields';
import { NotificationFlag } from '../../NotificationFlag/NotificationFlag';
import { NotificationFlagsCategories } from '../../../models/NotificationFlags/NotificationFlagsCategories';

interface SellerLegalDocsInfoInputFormProps {
    setIsSaving: (value: boolean) => void;
    isEditable?: boolean;
    timelineId?: number;
    onSaveSuccess?: () => void;
}

export interface SellerLegalDocsInfoInputFormRef {
    handleSubmit: () => void;
}

const SellerLegalDocsInfoInputForm = forwardRef<
    SellerLegalDocsInfoInputFormRef,
    SellerLegalDocsInfoInputFormProps
>((props, ref) => {
    const { isEditable = true, timelineId } = props;
    const axios = useStateSelector((state) => state.core.axios);
    const dispatch = useDispatch() as any;

    const sellerProfile = useStateSelector(
        (state) => state.profilesData.selectedPofile
    ) as MarketplaceSellerProfile;

    const statesByRegion = useStateSelector(
        (state) => state.locationsState.statesByRegion
    );

    const [stateOptions, setStateOptions] = useState<LocationStateItem[]>([]);
    const [editSellerDocsInfo, setEditSellerDocsInfo] =
        useState<SellerDocsInfo | null>(null);
    const zipCodeCache = useRef<Record<string, boolean>>({}).current;
    const formRef = useRef<any>(null);

    useEffect(() => {
        if (statesByRegion) {
            const indexedStates = getAllStatesIndexed(statesByRegion);
            setStateOptions(indexedStates);
        }
    }, [statesByRegion]);

    useEffect(() => {
        const docsInfo =
            sellerProfile?.sellerDocsInfo || createEmptySellerDocsInfo();
        setEditSellerDocsInfo(docsInfo);

        if (formRef.current) {
            formRef.current.setValues(docsInfo);
        }
    }, [sellerProfile]);

    const handleSubmit = (values: FormikValues) => {
        const updatedProfile: MarketplaceSellerProfile = {
            ...sellerProfile,
            sellerDocsInfo: values as SellerDocsInfo,
            type: 'marketplaceSellerProfile',
        };

        props.setIsSaving(true);

        dispatch(UpdateProfile(updatedProfile))
            .then(() => {
                props.setIsSaving(false);
                props.onSaveSuccess?.();
            })
            .catch((error: Error) => {
                console.error('Error updating seller legal docs info:', error);
                props.setIsSaving(false);
            });
    };

    useImperativeHandle(ref, () => ({
        handleSubmit: () => {
            if (formRef.current) {
                formRef.current.handleSubmit();
            }
        },
    }));

    const docsInfoFieldBadge = (field: string) =>
        timelineId ? (
            <NotificationFlag
                flagClassName="notification-flag-wrapper"
                doNotRemove={true}
                eventCode={`${NotificationFlagsCategories.MpCode}.${timelineId}.${NotificationFlagsCategories.SellerProfileUpdatedCode}.SellerDocsInfo.${field}`}
            />
        ) : null;

    if (!editSellerDocsInfo) {
        return null;
    }

    return (
        <div className="seller-legal-docs-info-input-form">
            <div className="legal-docs-form-container">
                <Form
                    innerRef={formRef}
                    onSubmit={handleSubmit}
                    initialValues={editSellerDocsInfo}
                    validationSchema={getSellerDocsInfoValidationSchema(
                        axios,
                        zipCodeCache
                    )}>
                    <Row>
                        <Col md={6} className="form-section">
                            <div className="section-header">
                                <h3>Legal Entity Information</h3>
                            </div>

                            <FormGroup>
                                <Row className="horizontal-form-group">
                                    <Col
                                        md={5}
                                        className="col-form-label"
                                        tag="label">
                                        <span className="label-with-badge">
                                            Full Legal Entity Name
                                            {docsInfoFieldBadge('FullLegalEntityName')}
                                        </span>
                                    </Col>
                                    <Col md={7}>
                                        <Input
                                            name="fullLegalEntityName"
                                            type="text"
                                            className="form-control"
                                            disabled={!isEditable}
                                        />
                                    </Col>
                                </Row>
                            </FormGroup>

                            <FormGroup>
                                <Row className="horizontal-form-group">
                                    <Col
                                        md={5}
                                        className="col-form-label"
                                        tag="label">
                                        <span className="label-with-badge">
                                            Type of Legal Entity
                                            {docsInfoFieldBadge('LegalEntityType')}
                                        </span>
                                    </Col>
                                    <Col md={7}>
                                        <Input
                                            name="legalEntityType"
                                            type="select"
                                            className="form-control"
                                            disabled={!isEditable}>
                                            <option value="">
                                                Select Type
                                            </option>
                                            {LegalEntityTypeList.map((item) => (
                                                <option
                                                    key={item.id}
                                                    value={item.id}>
                                                    {item.name}
                                                </option>
                                            ))}
                                        </Input>
                                    </Col>
                                </Row>
                            </FormGroup>

                            <FormGroup>
                                <Row className="horizontal-form-group">
                                    <Col
                                        md={5}
                                        className="col-form-label"
                                        tag="label">
                                        <span className="label-with-badge">
                                            State of Registration of Legal Entity
                                            {docsInfoFieldBadge('LegalEntityRegistrationState')}
                                        </span>
                                    </Col>
                                    <Col md={7}>
                                        <Input
                                            name="legalEntityRegistrationState"
                                            disabled={!isEditable}
                                            type="select"
                                            className="form-control">
                                            <option value="">
                                                Select State
                                            </option>
                                            {stateOptions.map((state) => (
                                                <option
                                                    key={state.id}
                                                    value={state.code}>
                                                    {state.code}
                                                </option>
                                            ))}
                                        </Input>
                                    </Col>
                                </Row>
                            </FormGroup>
                        </Col>

                        <Col md={6} className="form-section">
                            <SellerDocsInfoAddressInputFields
                                fieldPrefix="legalEntityAddress"
                                stateOptions={stateOptions}
                                title="Legal Entity Address"
                                isEditable={isEditable}
                                notificationFlagPrefix={timelineId ? `${NotificationFlagsCategories.MpCode}.${timelineId}.${NotificationFlagsCategories.SellerProfileUpdatedCode}.SellerDocsInfo.LegalEntityAddress` : undefined}
                            />
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6} className="form-section">
                            <div className="section-header">
                                <h3>Recipient of Notices</h3>
                            </div>

                            <FormGroup>
                                <Row className="horizontal-form-group">
                                    <Col
                                        md={5}
                                        className="col-form-label"
                                        tag="label">
                                        <span className="label-with-badge">
                                            Full Name
                                            {docsInfoFieldBadge('RecipientFullName')}
                                        </span>
                                    </Col>
                                    <Col md={7}>
                                        <Input
                                            name="recipientFullName"
                                            type="text"
                                            className="form-control"
                                            disabled={!isEditable}
                                        />
                                    </Col>
                                </Row>
                            </FormGroup>

                            <FormGroup>
                                <Row className="horizontal-form-group">
                                    <Col
                                        md={5}
                                        className="col-form-label"
                                        tag="label">
                                        <span className="label-with-badge">
                                            Title
                                            {docsInfoFieldBadge('RecipientTitle')}
                                        </span>
                                    </Col>
                                    <Col md={7}>
                                        <Input
                                            name="recipientTitle"
                                            type="text"
                                            className="form-control"
                                            disabled={!isEditable}
                                        />
                                    </Col>
                                </Row>
                            </FormGroup>
                        </Col>

                        <Col md={6} className="form-section">
                            <SellerDocsInfoAddressInputFields
                                fieldPrefix="recipientAddressInfo"
                                stateOptions={stateOptions}
                                title="Notice Recipient Address"
                                isEditable={isEditable}
                                notificationFlagPrefix={timelineId ? `${NotificationFlagsCategories.MpCode}.${timelineId}.${NotificationFlagsCategories.SellerProfileUpdatedCode}.SellerDocsInfo.RecipientAddressInfo` : undefined}
                            />
                        </Col>
                    </Row>
                </Form>
            </div>
        </div>
    );
});

export default SellerLegalDocsInfoInputForm;
